import React, { useEffect, useState } from "react";
import Modal from "../ui/Modal";
import Input from "../ui/Input";
import Textarea from "../ui/Textarea";
import Button from "../ui/Button";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as customerActions from "src/redux/slices/customerSlice";
import { Customer } from "src/types/customer.types";
import { toast } from "react-toastify";

interface AddCustomerModalProps {
  open: boolean;
  customer?: Customer | null;
  onClose: () => void;
  onSuccess: (customer?: Customer) => void;
}

type FormValues = {
  name: string;
  mobile: string;
  email: string;
  dob: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  pincode: string;
  gstNumber: string;
  notes: string;
};

const EMPTY_VALUES: FormValues = {
  name: "",
  mobile: "",
  email: "",
  dob: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  pincode: "",
  gstNumber: "",
  notes: "",
};

const AddCustomerModal: React.FC<AddCustomerModalProps> = ({
  open,
  customer = null,
  onClose,
  onSuccess,
}) => {
  const dispatch = useAppDispatch();
  const { create, edit } = useAppSelector((state) => state.customer);

  const [values, setValues] = useState<FormValues>(EMPTY_VALUES);
  const [errors, setErrors] = useState<Partial<Record<keyof FormValues, string>>>(
    {}
  );

  useEffect(() => {
    setValues({
      name: customer?.name || "",
      mobile: customer?.mobile || "",
      email: customer?.email || "",
      dob: customer?.dob || "",
      addressLine1: customer?.addressLine1 || "",
      addressLine2: customer?.addressLine2 || "",
      city: customer?.city || "",
      state: customer?.state || "",
      pincode: customer?.pincode || "",
      gstNumber: customer?.gstNumber || "",
      notes: customer?.notes || "",
    });
    setErrors({});
  }, [customer]);

  useEffect(() => {
    if (create.data) {
      toast.success("Customer created successfully");
      const created = create.data;
      dispatch(customerActions.resetCustomerCreate());
      onSuccess(created);
    } else if (create.error) {
      toast.error(`Error: ${create.error}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [create]);

  useEffect(() => {
    if (edit.data) {
      toast.success("Customer updated successfully");
      const updated = edit.data;
      dispatch(customerActions.resetEditCustomer());
      onSuccess(updated);
    } else if (edit.error) {
      toast.error(`Error: ${edit.error}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edit]);

  const handleChange = (field: keyof FormValues, value: string) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validate = () => {
    const newErrors: Partial<Record<keyof FormValues, string>> = {};
    if (!values.name.trim()) newErrors.name = "Customer name is required";
    if (!values.mobile.trim()) newErrors.mobile = "Mobile number is required";
    if (!values.addressLine1.trim())
      newErrors.addressLine1 = "Address Line 1 is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    if (customer?._id) {
      dispatch(customerActions.editCustomerRequest({ id: customer._id, data: values }));
    } else {
      dispatch(customerActions.createCustomerRequest({ customer: values }));
    }
  };

  const loading = create.loading || edit.loading;
  const title = customer?._id ? "Edit Customer" : "Add Customer";

  return (
    <Modal open={open} onClose={onClose} title={title} size="2xl">
      <form className="space-y-6" onSubmit={handleSubmit}>
        <div>
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Basic details
          </h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="Customer Name"
              required
              value={values.name}
              onChange={(e) => handleChange("name", e.target.value)}
              error={!!errors.name}
              helperText={errors.name}
            />
            <Input
              label="Mobile"
              required
              value={values.mobile}
              onChange={(e) => handleChange("mobile", e.target.value)}
              error={!!errors.mobile}
              helperText={errors.mobile}
            />
            <Input
              label="Email"
              type="email"
              value={values.email}
              onChange={(e) => handleChange("email", e.target.value)}
            />
            <Input
              label="Date of Birth"
              type="date"
              optional
              value={values.dob}
              onChange={(e) => handleChange("dob", e.target.value)}
            />
            <Input
              label="GST Number"
              optional
              value={values.gstNumber}
              onChange={(e) => handleChange("gstNumber", e.target.value)}
            />
          </div>
        </div>

        <div>
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Address
          </h3>
          <div className="grid grid-cols-1 gap-4">
            <Input
              label="Address Line 1"
              required
              value={values.addressLine1}
              onChange={(e) => handleChange("addressLine1", e.target.value)}
              error={!!errors.addressLine1}
              helperText={errors.addressLine1}
            />
            <Input
              label="Address Line 2"
              optional
              value={values.addressLine2}
              onChange={(e) => handleChange("addressLine2", e.target.value)}
            />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Input
                label="City"
                value={values.city}
                onChange={(e) => handleChange("city", e.target.value)}
              />
              <Input
                label="State"
                value={values.state}
                onChange={(e) => handleChange("state", e.target.value)}
              />
              <Input
                label="Pincode"
                value={values.pincode}
                onChange={(e) => handleChange("pincode", e.target.value)}
              />
            </div>
          </div>
        </div>

        <Textarea
          label="Notes"
          optional
          rows={3}
          value={values.notes}
          onChange={(e) => handleChange("notes", e.target.value)}
          placeholder="Any additional notes about this customer..."
        />

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-4 dark:border-gray-700">
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" isLoading={loading} disabled={loading}>
            {customer?._id ? "Save changes" : "Add customer"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default AddCustomerModal;
